import isNil from 'lodash/isNil';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  useForm,
  FormProvider,
} from 'react-hook-form';
import {
  InsuranceDTO,
} from 'dtos';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import Controller from 'core/components/FormController';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import PatientInsuranceSelect, {
  InsuranceLabel,
} from 'pages/Dashboard/components/PatientInsuranceSelect';
import {
  Claim,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useNavigate,
} from 'react-router-dom';
import {
  useNotify,
} from 'core/hooks';
import {
  getErrorMessage,
} from 'utils/errors';
import {
  useCreateSecondaryClaim,
  useGetClaimById,
} from 'pages/Dashboard/pages/Billing/hooks';
import ClaimSelect from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimSelect';
import head from 'lodash/head';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  ClaimSummaryDTO,
} from 'dtos/claimSummaryDTO';
import Tag from 'core/components/Tag';
import {
  useGetPatientInsurances,
} from 'views/EMR/components/Insurance/hooks';

type Props = {
  patientId: number;
  claim: Claim;
  onClose: () => void;
};

export type SecondaryClaimDialogParams = {
  claim: Claim;
  patientId: number;
};

type SecondaryClaimForm = {
  insurance?: InsuranceDTO | null;
  claim?: ClaimSummaryDTO | null;
};

export default function SecondaryClaimDialog({
  patientId,
  claim: _claim,
  onClose,
}: Props) {
  const notify = useNotify();
  const navigate = useNavigate();

  const { data } = useGetClaimById(_claim?.claimId);
  const { data: patientInsurances } = useGetPatientInsurances(patientId);

  const {
    allClaimsForEncounter,
    claimAcknowledgementStatuses,
    claim,
  } = data ?? {};

  const claimOptions = useMemo(
    () => allClaimsForEncounter?.filter(
      (claim: ClaimSummaryDTO) => (
        !isNil(claim?.claimId) && claim?.secondaryClaimActionState?.isEnabled
      ),
    ),
    [allClaimsForEncounter],
  );

  const formProps = useForm<SecondaryClaimForm>({
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, dirtyFields },
    getValues,
    resetField,
    setValue,
  } = formProps;

  const previousPatientId = useRef(patientId);

  useEffect(() => {
    if (previousPatientId.current !== patientId) {
      resetField('insurance', { defaultValue: null });
      previousPatientId.current = patientId;
    }
  }, [patientId, resetField]);

  useEffect(() => {
    if (dirtyFields.insurance || getValues('insurance')) {
      return;
    }

    const activeSecondaryInsurances = patientInsurances?.filter(
      (insurance) => !insurance.inactive && insurance.insuranceSlot === InsuranceSlot.Secondary,
    );

    if (activeSecondaryInsurances?.length === 1) {
      setValue('insurance', activeSecondaryInsurances[0], { shouldValidate: true });
    }
  }, [dirtyFields.insurance, getValues, patientId, patientInsurances, setValue]);

  useEffect(() => {
    setValue('claim', (claim as ClaimSummaryDTO | null | undefined) ?? null, { shouldValidate: true });
  }, [claim, setValue]);

  const { mutateAsync: createSecondaryClaim } = useCreateSecondaryClaim();

  const onSubmit = async ({ insurance, claim }: SecondaryClaimForm) => {
    try {
      const claimBundle = await createSecondaryClaim({
        primaryClaimId: claim?.claimId,
        insuranceId: insurance?.insuranceId,
      });
      navigate(`/billing/claims/${claimBundle?.claim?.claimId ?? 0}`);
      notify({
        message: 'Claim splits were saved.',
        variant: 'success',
      });
      onClose();
    } catch (e: unknown) {
      console.error(e);
      if (!getErrorMessage(e)) {
        notify({
          message: 'Oops, Something went wrong. Try again!',
          variant: 'error',
        });
      }
    }
  };

  const getInsuranceLabel = useCallback((insurance: InsuranceDTO) => (
    <InsuranceLabel
      option={insurance}
      additionalTag={claim?.insuranceDetails?.insuranceId === insurance?.insuranceId ? (
        <Tag
          tagName={claim?.status ?? ''}
          className="font-medium"
        />
      ) : undefined}
    />
  ), [claim]);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Create Secondary Claim"
    >
      <div className="flex flex-col h-full w-full justify-between gap-4">
        <FormProvider {...formProps}>
          <div className="populate-form flex-1 flex w-full flex-col gap-form-row">
            <Controller
              control={control}
              name="claim"
              rules={{ required: 'Select claim' }}
              render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
                <Wrapper
                  id="claim"
                  label="Claim"
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full"
                >
                  <ClaimSelect
                    {...field}
                    acknowledgementStatus={head(claimAcknowledgementStatuses)}
                    options={claimOptions ?? []}
                    value={value as ClaimSummaryDTO}
                    onChange={onChange}
                  />
                </Wrapper>
              )}
            />
            <Controller
              control={control}
              name="insurance"
              rules={{ required: 'Select Insurance' }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="patient-insurance"
                  label="Patient Insurance"
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full"
                >
                  <PatientInsuranceSelect
                    patientId={patientId}
                    insuranceId={!isNil(field.value) && typeof field.value === 'object' && 'insuranceId' in field.value
                      ? field.value.insuranceId ?? undefined
                      : undefined}
                    onChange={(insurance: InsuranceDTO | InsuranceDTO[]) => {
                      field.onChange((insurance as InsuranceDTO));
                    }}
                    getLabel={getInsuranceLabel}
                    editable={false}
                    placeholder="Select"
                  />
                </Wrapper>
              )}
            />
          </div>
          <div className="flex gap-2 mb-2 justify-end w-full">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !isValid}
              >
                Confirm
              </Button>
            </DisabledFormTooltip>
          </div>
        </FormProvider>
      </div>
    </Dialog>
  );
}
